/**
 * This function retrieves the younger siblings of a given item.
 * - If one of the siblings is a loop, all items inside the loop will be removed, and an empty array will be returned because items inside a loop are not accessible by the following items.
 * - If one of the siblings is a filter, the items will not be removed because the items might or might not be available.
 *
 * This function assumes that scheduled actions always follow delayed actions, as provided by the backend. The backend must send the items in the correct order.
 *
 * If the item found by the itemID is a delayed action, all scheduled actions must be added. This is because we don't know when the delayed action will be executed compared to the scheduled action, so they might be available. We do not need to do the same when the action found by the itemID is scheduled because delayed actions will always precede scheduled actions in the array, so they will be added by the function. Finally, custom delays are always last, so both delayed and scheduled actions will be added.
 *
 * @param {Number} itemID - The ID of the item.
 * @param {Object[]} items - An array of items.
 * @return {Object[]} An array of the younger siblings.
 */
const getYoungerSiblings = ( itemID, items = [] ) => {
	// Clone the items to avoid making any changes to the original array
	items = structuredClone( items );

	// Create the array that will hold the younger siblings
	const youngerSiblings = [];

	// Check if the item is in the list at all first, otherwise return an empty array
	if ( ! items.some( item => item.id === itemID ) ) {
		return youngerSiblings;
	}

	// Get all the scheduled items
	const scheduledItems = items.filter(
		item => item.miscellaneous?.delay_schedule?.MODE?.value === 'schedule'
	);

	// Iterate through the items and stop once we find the target item
	items.every( item => {
		if ( item.id !== itemID ) {
			// If the item is a loop, we need to remove all items inside the loop
			if ( item.type === 'loop' ) {
				item.items = [];
			}

			youngerSiblings.push( item );

			// Continue iteration
			return true;
		}

		// If the target item is a delayed action, we need to add all the scheduled actions before the first delayed action
		if ( item.miscellaneous?.delay_schedule?.MODE?.value === 'delay' ) {
			// Find the index of the first delayed action
			const firstDelayedActionIndex = youngerSiblings.findIndex(
				item => item.miscellaneous?.delay_schedule?.MODE?.value === 'delay'
			);

			if ( firstDelayedActionIndex === -1 ) {
				// If there are no delayed actions, add the scheduled actions at the end
				youngerSiblings.push( ...scheduledItems );
			} else {
				// Add the scheduled actions before the first delayed action
				youngerSiblings.splice( firstDelayedActionIndex, 0, ...scheduledItems );
			}
		}

		// Add the target item itself
		youngerSiblings.push( item );

		// Stop iteration
		return false;
	} );

	return youngerSiblings;
};

/**
 * Processes child items of a given parent item, searching for and modifying a specific item by ID.
 *
 * @param {String} itemID - The ID of the item to find and modify.
 * @param {Object} parentItem - The parent item containing the child items to search through.
 * @param {String} childItemsKey - The key in the parentItem that contains the array of child items.
 * @param {Array} items - The original array of items, used for determining younger siblings.
 * @return {Object|null} An object containing a 'found' flag and modified items if the item is found; otherwise, null.
 */
const processChildItems = ( itemID, parentItem, childItemsKey, items ) => {
	const childResult = findAndModifyItemRecursively( itemID, parentItem[ childItemsKey ] );

	// If the child item was found, modify the parent item and return the modified items
	if ( childResult && childResult.found ) {
		parentItem[ childItemsKey ] = childResult.modifiedItems;

		// If the item is a loop filter, we need to remove all items inside the loop
		if ( childResult.item?.type === 'loop-filter' ) {
			parentItem.items = [];
		}

		// If the item is a filter condition, we need to remove all items inside the filter (parent)
		if ( childResult.item?.type === 'filter-condition' ) {
			parentItem.items = [];
		}

		return {
			found: true,
			modifiedItems: getYoungerSiblings( parentItem.id, items )
		};
	}

	return null;
};

/**
 * Recursively searches for an item by its ID, modifies its nested items if necessary, and returns the item along with its modified structure. It evaluates the siblings of the item and each ancestor, obtaining the correct siblings of the item, parent, grandparent, and so on.
 *
 * @param {Number} itemID - The ID of the item to find.
 * @param {Object[]} items - An array of items.
 * @return {Object} An object containing a flag indicating if the item was found and the modified items array.
 */
const findAndModifyItemRecursively = ( itemID, items ) => {
	for ( const item of items ) {
		if ( item.id === itemID ) {
			// If the item itself that we're getting has items, conditions or filters, don't return them because the items inside the item don't run by the time the item is run
			if ( item.items ) {
				item.items = [];
			}

			if ( item.conditions ) {
				item.conditions = [];
			}

			if ( item.filters ) {
				item.filters = [];
			}

			return {
				found: true,
				modifiedItems: getYoungerSiblings( itemID, items ),
				item
			};
		}

		if ( item.items && item.items.length > 0 ) {
			const result = processChildItems( itemID, item, 'items', items );

			if ( result ) {
				return result;
			}
		}

		if ( item.type === 'filter' && item.conditions && item.conditions.length > 0 ) {
			const result = processChildItems( itemID, item, 'conditions', items );

			if ( result ) {
				return result;
			}
		}

		if ( item.type === 'loop' && item.filters && item.filters.length > 0 ) {
			const result = processChildItems( itemID, item, 'filters', items );

			if ( result ) {
				return result;
			}
		}
	}

	return {
		found: false,
		modifiedItems: items
	};
};

/**
 * Retrieves the actions flow path by recursively finding younger siblings at each ancestor level.
 *
 * @param {Number} itemID - The ID of the item.
 * @return {Object[]} An array of all younger siblings at each ancestor level.
 */
const getItemsActionsFlowPath = ( itemID ) => {
	// Get the actions from the main object, but clone it first to avoid mutation
	const actions = structuredClone( UncannyAutomator._recipe.actions.items );

	// Recursively search for the item and get the entire modified structure
	const result = findAndModifyItemRecursively( itemID, actions );

	// Return the entire modified structure with all ancestors
	return result.modifiedItems;
}

export default getItemsActionsFlowPath;
